import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import api from '../../services/api.js'
import SignaturePad from '../common/SignaturePad.jsx'
import { useAuth } from '../../hooks/useAuth'

const STEPS = [
  { key: 'identity_submitted', label: 'Submit Identity Documents', component: 'identity' },
  { key: 'kyc_verified', label: 'Personal Details', component: 'kyc' },
  // One combined KYC consent (NDA + Policies + Privacy) reviewed & signed once.
  { key: 'kyc_consent_signed', label: 'Sign Onboarding Consent', component: 'kyc_consent' },
]

// Normalise a date the ID reader returned into the ISO form <input type="date"> requires.
// Real documents print DD/MM/YYYY (Aadhaar, licences) as often as ISO, so accept both.
function toIsoDate(raw) {
  const v = (raw || '').trim()
  if (!v) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v
  const m = v.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/)   // DD/MM/YYYY
  if (m) {
    const [, d, mo, y] = m
    return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`
  }
  return ''
}

export default function MyOnboarding() {
  const { t } = useTranslation('onboarding')
  const { user } = useAuth()
  const [checklist, setChecklist] = useState([])
  const [activeStep, setActiveStep] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  // Identity form state
  const [identityForm, setIdentityForm] = useState({
    passport_number: '', nationality: '', date_of_birth: '', mailing_address: ''
  })
  // What we already hold from the ID uploaded at signup. Pre-filling from this is the difference
  // between "produce your document and type it out again" and "check these details and submit".
  const [prefill, setPrefill] = useState(null)
  useEffect(() => {
    let cancelled = false
    api.get('/onboarding/identity/prefill')
      .then(({ data }) => {
        if (cancelled || !data) return
        setPrefill(data)
        // Only fill blanks — never overwrite something the consultant has already typed.
        setIdentityForm(f => ({
          passport_number: f.passport_number || data.id_number || '',
          nationality: f.nationality || data.nationality || '',
          date_of_birth: f.date_of_birth || data.date_of_birth || '',
          mailing_address: f.mailing_address || data.mailing_address || '',
        }))
      })
      .catch(() => {})   // pre-fill is a convenience; the form still works without it
    return () => { cancelled = true }
  }, [])
  // Auto-fill the KYC identity fields from an uploaded ID (passport / national ID / …).
  const [idParsing, setIdParsing] = useState(false)
  const [idFillMsg, setIdFillMsg] = useState(null)
  const autofillFromId = async (file) => {
    if (!file) return
    if (!/\.(pdf|png|jpe?g|webp)$/i.test(file.name || '')) { setIdFillMsg({ err: true, text: 'Upload an image (PNG/JPG) or PDF of your ID.' }); return }
    if (file.size > 10 * 1024 * 1024) { setIdFillMsg({ err: true, text: 'File too large (max 10 MB).' }); return }
    setIdParsing(true); setIdFillMsg(null)
    try {
      const fd = new FormData(); fd.append('file', file)
      const { data } = await api.post('/auth/parse-id', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      // Accept the formats real IDs actually print, not just ISO. Aadhaar and many licences show
      // DD/MM/YYYY; the old ISO-only test silently threw those away, so the date of birth stayed
      // blank even though the reader had found it. The <input type="date"> still needs ISO, so
      // convert rather than reject.
      const isoDob = toIsoDate(data?.date_of_birth)
      setIdentityForm(f => ({
        ...f,
        passport_number: data?.id_number || f.passport_number,
        nationality: data?.nationality || f.nationality,
        date_of_birth: isoDob || f.date_of_birth,
        mailing_address: data?.address || f.mailing_address,
      }))
      const got = [data?.id_number && 'ID number', data?.nationality && 'nationality', isoDob && 'date of birth', data?.address && 'address'].filter(Boolean)
      setIdFillMsg(got.length
        ? { err: false, text: `Auto-filled ${got.join(', ')} from your ID. Please double-check and complete the rest.` }
        : { err: true, text: "Couldn't read the details automatically — please type them in." })
    } catch (e) {
      setIdFillMsg({ err: true, text: e.response?.data?.detail || "Couldn't read this ID. Try a clearer photo, or type the details in." })
    } finally { setIdParsing(false) }
  }

  // Shared signature state for the NDA/Policies/Consent steps — only one accordion
  // panel is open at a time, so one pair of fields covers whichever is active.
  const [sigName, setSigName] = useState('')
  const [sigData, setSigData] = useState(null)

  useEffect(() => { fetchChecklist() }, [])

  const fetchChecklist = async () => {
    try {
      const { data } = await api.get('/onboarding/checklist')
      setChecklist(data)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const completedCount = checklist.filter(i => i.completed).length
  const totalCount = checklist.length || STEPS.length
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  const isItemComplete = (key) => checklist.find(i => i.item_key === key)?.completed

  const submitIdentity = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await api.post('/onboarding/identity', identityForm)
      fetchChecklist()
      setActiveStep(null)
    } catch (e) {
      alert(e.response?.data?.detail || 'Error submitting identity')
    } finally {
      setSubmitting(false)
    }
  }

  const resetSignature = () => { setSigName(''); setSigData(null) }

  // View the consultant's own signed KYC consent in a new tab (fetch HTML with auth → blob URL).
  const viewMyConsent = async () => {
    try {
      const resp = await api.get('/onboarding/consent/me/kyc/view', { responseType: 'blob' })
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: 'text/html' }))
      window.open(blobUrl, '_blank')
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not open your consent record.')
    }
  }

  // Download the consultant's own signed KYC consent (.docx or a pre-signed cloud URL).
  const downloadMyConsent = async () => {
    try {
      const resp = await api.get('/onboarding/consent/me/kyc/download', { responseType: 'blob' })
      const contentType = resp.data?.type || ''
      if (contentType.includes('application/json')) {
        const { url } = JSON.parse(await resp.data.text())
        window.open(url, '_blank')
      } else {
        const blobUrl = URL.createObjectURL(resp.data)
        const a = document.createElement('a')
        a.href = blobUrl; a.download = 'Onboarding_Consent.docx'
        document.body.appendChild(a); a.click(); document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not download your consent record.')
    }
  }

  const submitKycConsent = async () => {
    if (!sigName.trim()) { alert('Please type your full name to sign.'); return }
    setSubmitting(true)
    try {
      await api.post('/onboarding/kyc-consent', { signed_name: sigName.trim(), signature_data: sigData })
      fetchChecklist()
      setActiveStep(null)
      // Keep the typed name/signature visible after signing (the step now shows as signed) —
      // clearing it made users think the signature didn't take.
    } catch (e) {
      alert(e.response?.data?.detail || 'Error signing the onboarding consent')
    } finally {
      setSubmitting(false)
    }
  }

  // "My Onboarding" (identity → KYC → consent → activate your CONSULTANT account) is a
  // consultant-only flow. A PM/Admin/Finance/Funder landing here (e.g. via a stale link)
  // should not see consultant activation steps — send them to the pipeline instead.
  if (user && user.role !== 'Consultant') {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('pipeline2.my_onboarding')}</h1>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          This page is the <strong>consultant</strong> onboarding checklist — it doesn't apply to your role.
          {(user.role === 'Admin' || user.role === 'Project Manager') && (
            <> To review or activate consultants, go to the <Link to="/onboarding" className="font-semibold text-amber-900 underline">{t('pipeline2.pipeline_title')}</Link>.</>
          )}
        </div>
      </div>
    )
  }

  if (loading) return <div className="p-6 text-gray-500">{t('pipeline2.loading')}</div>

  return (
    <div className="max-w-2xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('pipeline2.my_onboarding')}</h1>
      <p className="text-gray-500 mb-6 text-sm">
        Complete all steps below to activate your consultant account.
      </p>

      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-gray-500 mb-1">
          <span>{completedCount} of {totalCount} steps complete</span>
          <span>{progress}%</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full">
          <div
            className="h-2 bg-green-500 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="space-y-3">
        {STEPS.map((step, idx) => {
          const done = isItemComplete(step.key)
          return (
            <div key={step.key} className={`rounded-xl border p-4 ${done ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    done ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {done ? '✓' : idx + 1}
                  </div>
                  <div>
                    <p className={`font-medium ${done ? 'text-green-700' : 'text-gray-800'}`}>
                      {step.label}
                    </p>
                    {step.key === 'kyc_verified' && !done && (
                      <p className="text-xs text-gray-400">{t('pipeline2.completed_by_admin')}</p>
                    )}
                  </div>
                </div>
                {!done && step.key !== 'kyc_verified' && (
                  <button
                    onClick={() => { setActiveStep(activeStep === step.key ? null : step.key); resetSignature() }}
                    className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700"
                  >
                    Start
                  </button>
                )}
                {done && (
                  <div className="flex items-center gap-3">
                    {step.key === 'kyc_consent_signed' && (
                      <>
                        <button onClick={viewMyConsent} className="text-xs text-blue-600 hover:text-blue-800 font-medium" title={t('pipeline2.t_open_kyc')}>
                          View
                        </button>
                        <button onClick={downloadMyConsent} className="text-xs text-blue-600 hover:text-blue-800 font-medium" title={t('pipeline2.t_download_kyc')}>
                          Download
                        </button>
                      </>
                    )}
                    <span className="text-green-600 text-sm font-medium">{t('checklist.complete')}</span>
                  </div>
                )}
              </div>

              {/* Inline step forms */}
              {activeStep === step.key && step.component === 'identity' && (
                <form onSubmit={submitIdentity} className="mt-4 space-y-3 border-t pt-4">
                  <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
                    Your identity documents are encrypted and stored securely.
                  </p>
                  {/* Already have their ID from signup → confirm, don't ask again. */}
                  {prefill?.prefilled_from === 'signup' && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                      <p className="text-sm font-semibold text-emerald-800">
                        ✓ We already have your {prefill.id_document_type || 'ID'}
                        {prefill.has_id_document ? ' and the document you uploaded when you signed up' : ''}
                      </p>
                      <p className="text-[11px] text-emerald-700 mt-1">
                        The details below have been filled in from it. <b>Please check they're correct,
                        change anything that's wrong, then press Submit Identity.</b> You do not need to
                        upload your ID again.
                      </p>
                    </div>
                  )}

                  {/* Upload an ID to auto-fill — only needed when we don't already hold one. */}
                  {prefill?.prefilled_from !== 'signup' && (
                    <div className="rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/50 p-3">
                      <label className="flex items-center justify-center gap-2 cursor-pointer text-sm font-medium text-blue-700">
                        {idParsing ? '⏳ Reading your ID…' : '📄 Auto-fill from your ID'}
                        <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp" disabled={idParsing}
                          onChange={e => { autofillFromId(e.target.files?.[0]); e.target.value = '' }} />
                      </label>
                      <p className="text-[10px] text-gray-500 text-center mt-1">We'll read your ID number, nationality &amp; date of birth for you to confirm.</p>
                      {idFillMsg && <p className={`text-[11px] mt-1 text-center ${idFillMsg.err ? 'text-amber-600' : 'text-emerald-600'}`}>{idFillMsg.text}</p>}
                    </div>
                  )}
                  {[
                    ['passport_number', prefill?.id_document_type ? `${prefill.id_document_type} Number` : 'ID / Passport Number', 'text', true],
                    ['nationality', 'Nationality', 'text', true],
                    ['date_of_birth', 'Date of Birth', 'date', false],
                    ['mailing_address', 'Personal mailing address', 'text', true],
                  ].map(([field, label, type, required]) => {
                    const looksLikeEmail = field === 'mailing_address' && /\S+@\S+\.\S+/.test(identityForm[field] || '');
                    return (
                    <div key={field}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                      <input
                        type={type}
                        value={identityForm[field]}
                        onChange={e => setIdentityForm(f => ({ ...f, [field]: e.target.value }))}
                        required={required}
                        placeholder={field === 'mailing_address' ? 'Your postal/home address (not an email)' : undefined}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                      {looksLikeEmail && (
                        <p className="text-[11px] text-amber-600 mt-1">That looks like an email address. Please enter your postal/home mailing address here.</p>
                      )}
                    </div>
                    );
                  })}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    {submitting ? 'Submitting...' : 'Submit Identity'}
                  </button>
                </form>
              )}

              {activeStep === step.key && step.component === 'kyc_consent' && (
                <div className="mt-4 border-t pt-4">
                  <p className="text-sm text-gray-600 mb-3">
                    Please review all three sections below, then sign once to accept them together.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 mb-4 max-h-60 overflow-y-auto space-y-4">
                    <div>
                      <p className="font-medium mb-1">1. Non-Disclosure Agreement</p>
                      <p>By accepting this NDA, you agree to maintain the confidentiality of all proprietary information, client data, and business processes you encounter during your engagement with Three Pillars Data and Training Centre LLC.</p>
                    </div>
                    <div>
                      <p className="font-medium mb-1">2. Company Policies</p>
                      <p>By accepting these policies, you agree to comply with all applicable company guidelines including data protection, professional conduct, conflict of interest, and communication standards.</p>
                    </div>
                    <div>
                      <p className="font-medium mb-1">3. Privacy Consent</p>
                      <p>I consent to the processing of my personal data for the purpose of consultant engagement management, in accordance with the UAE Federal Decree-Law No. 45/2021 and applicable data protection regulations.</p>
                    </div>
                  </div>
                  <SignaturePad name={sigName} onNameChange={setSigName} signatureDataUrl={sigData} onSignatureChange={setSigData} />
                  <button
                    onClick={submitKycConsent}
                    disabled={submitting || !sigName.trim()}
                    className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    {submitting ? 'Processing...' : 'I Agree & Sign'}
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {progress === 100 && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-4 text-center">
          <p className="text-green-700 font-semibold">{t('pipeline2.all_steps_complete')}</p>
          <p className="text-green-600 text-sm mt-1">{t('pipeline2.admin_will_review')}</p>
        </div>
      )}
    </div>
  )
}
